import {Injectable, Injector} from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {ApiService} from './api.service';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { environment } from '../../environments/environment';
import { LoggedUserModel } from 'app/models';
import { WidgetService } from '../shared/services/widget.service';
import { KindeAuthService } from './kinde-auth.service';
import { AppStorage } from 'app/utilities/storage';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_INFO } from 'app/shared/constants';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppLoadService {
  protected httpClient: HttpClient;
  protected router: Router;
  protected apiService: ApiService;
  protected userService: UserService;
  protected widgetService: WidgetService;
  protected kindeAuth: KindeAuthService;

  constructor(private injector: Injector) {
    this.httpClient = this.injector.get(HttpClient);
    this.router = this.injector.get(Router);
    this.apiService = this.injector.get(ApiService);
    this.userService = this.injector.get(UserService);
    this.widgetService = this.injector.get(WidgetService);
    this.kindeAuth = this.injector.get(KindeAuthService);
  }

  /**
   * Initializes the AI chat widget once the widget script has loaded.
   * The script tag uses `defer` so it may not be available immediately at
   * app init time. We poll briefly (up to 3 seconds) for window.SZWidget.
   */
  private initWidgetWhenReady(): void {
    let attempts = 0;
    const maxAttempts = 30; // 30 × 100ms = 3 seconds
    const interval = setInterval(() => {
      attempts++;
      if (window.SZWidget) {
        clearInterval(interval);
        this.widgetService.init();
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * Kinde-based app initialization.
   *
   * Flow:
   * 1. Initialize the Kinde SDK (processes any ?code=&state= callback in the URL)
   * 2. Check if the user has an active Kinde session
   * 3. If authenticated: populate user info from Kinde token claims, proceed to app
   * 4. If not authenticated: redirect to Kinde login
   *
   * The Kinde access token is sent as Authorization: Bearer on every API request
   * by the auth interceptor. The backend JwtStrategy accepts both SZ and Kinde
   * tokens — it detects Kinde tokens by the `kid` header and verifies via JWKS.
   * Company resolution happens server-side from the email claim.
   */
  async initAppWithKinde(): Promise<boolean> {
    // Admin impersonation: ?code=<jwt>&force_login=true bypasses Kinde
    // and exchanges the bridge JWT for SZ tokens via the legacy auth endpoint.
    const bridgeHandled = await this.handleBridgeCodeIfPresent();
    if (bridgeHandled) {
      this.initWidgetWhenReady();
      return true;
    }

    try {
      await this.kindeAuth.initialize();
    } catch {
      this.redirectToKindeLogin();
      return false;
    }

    const authenticated = await this.kindeAuth.isAuthenticated();

    if (!authenticated) {
      this.redirectToKindeLogin();
      return false;
    }

    // Populate user info from Kinde token claims.
    // company_id is 0 here — the backend resolves it from the email claim
    // in the Kinde access token on every authenticated request.
    const kindeUser = this.kindeAuth.getUser();
    if (kindeUser) {
      const givenName = kindeUser.given_name ?? '';
      const familyName = kindeUser.family_name ?? '';
      const fullName = [givenName, familyName].filter(Boolean).join(' ')
        || kindeUser.email?.split('@')[0]
        || '';
      const userName = givenName || kindeUser.email?.split('@')[0] || '';

      this.userService.setCurrentUserInfo(
        new LoggedUserModel(0, kindeUser.email ?? '', userName, fullName, undefined)
      );
    }

    this.initWidgetWhenReady();
    return true;
  }

  /**
   * Handles the admin impersonation bridge code (?code=<jwt>&force_login=true).
   * Exchanges the JWT with the backend for SZ access/refresh tokens, stores them,
   * and cleans up the URL. Returns true if a bridge code was handled.
   */
  private async handleBridgeCodeIfPresent(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const forceLogin = params.get('force_login');

    // Only handle ?code= when force_login=true and no state= (state= means Kinde callback)
    if (!code || forceLogin !== 'true' || params.has('state')) {
      return false;
    }

    try {
      const res = await lastValueFrom(
        this.httpClient.post<any>(`${this.apiService.apiHost}/api/auth`, { wp_access_token: code })
      );

      if (res?.data?.access_token) {
        AppStorage.storeTokenData(ACCESS_TOKEN_KEY, res.data.access_token);
        AppStorage.storeTokenData(REFRESH_TOKEN_KEY, res.data.refresh_token);

        // Decode the access token to get user info
        try {
          const payload = JSON.parse(atob(res.data.access_token.split('.')[1]));
          this.userService.setCurrentUserInfo(
            new LoggedUserModel(
              payload.company_id ?? 0,
              payload.user_email ?? '',
              payload.user_name ?? '',
              payload.full_name ?? '',
              undefined
            )
          );
        } catch {
          // Token decode failed — still authenticated, just no user info
        }

        // Mark as impersonation session and clean the URL
        localStorage.setItem('sz_impersonating', 'true');
        window.history.replaceState({}, '', window.location.pathname);
        return true;
      }
    } catch (err) {
      console.error('Bridge code exchange failed:', err);
    }
    return false;
  }

  /**
   * Redirects to the Kinde login page.
   * Uses the Kinde SDK `login()` which performs the PKCE redirect.
   */
  redirectToKindeLogin(): void {
    this.kindeAuth.login().catch(() => {
      // If Kinde login() itself throws (SDK not ready), fall back to the
      // legacy marketing site login URL so the user isn't stuck.
      window.location.href = `${environment.wpUrl}/sign-in?redirect=${encodeURIComponent(environment.uiBaseUrl || location.origin)}`;
    });
  }
}
